import { useMemo } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import type { Entity } from "@common/types/entity";
import type { Task } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import { getEntityAvatarName } from "@components/avatar/entityAvatarName";
import { useEntities } from "@hooks/useEntities";
import { useTaskClientName } from "../taskClientNamesContext";

/** What a card's context line shows: the client on the organization page, the task's entities when the card shows them. */
export interface TaskCardContext {
    clientWorkspaceId: string | undefined;
    entityIds: readonly number[];
}

/** A card's task and the fields it shows: List's visible columns, minus those the columns and lanes show. */
export interface TaskCardContextArgs {
    task: Task;
    cardFields: readonly string[];
    hiddenFields: ReadonlySet<string>;
    isCrossWorkspace: boolean;
}

const NO_ENTITY_IDS: readonly number[] = [];

/**
 * The card's context line, from the fields it shows: the client unless the
 * columns or lanes show it, and the entities when the Entities field is shown
 * and the task has some.
 */
export const getTaskCardContext = ({
    task,
    cardFields,
    hiddenFields,
    isCrossWorkspace,
}: TaskCardContextArgs): TaskCardContext => ({
    clientWorkspaceId:
        isCrossWorkspace && !hiddenFields.has(TaskColumn.CLIENT)
            ? task.workspaceId
            : undefined,
    entityIds:
        cardFields.includes(TaskColumn.ENTITIES) &&
        !hiddenFields.has(TaskColumn.ENTITIES)
            ? (task.entityIds ?? NO_ENTITY_IDS)
            : NO_ENTITY_IDS,
});

/** The workspace's entities among `entityIds`, in the workspace's order. */
export const useTaskCardEntities = (entityIds: readonly number[]) => {
    const entities = useEntities();
    return useMemo(() => {
        const taskEntityIds = new Set(entityIds);
        return entities.filter((entity) => taskEntityIds.has(entity.id));
    }, [entities, entityIds]);
};

/** `Client · Entity`, either part alone, or a count for several entities. */
export const joinTaskCardContext = ({
    clientName,
    entities,
}: {
    clientName: string | undefined;
    entities: ReadonlyArray<Pick<Entity, "name">>;
}) => {
    const [firstEntity] = entities;
    return [
        clientName,
        entities.length > 1
            ? pluralizeWithCount("entity", entities.length)
            : firstEntity && getEntityAvatarName(firstEntity),
    ]
        .filter(Boolean)
        .join(" · ");
};

/**
 * A card's accessible name: the title, then its context line's text, so the
 * name doesn't pick up the card's controls (the kebab's label).
 */
export const useTaskCardLabel = (args: TaskCardContextArgs) => {
    const { clientWorkspaceId, entityIds } = getTaskCardContext(args);
    const clientName = useTaskClientName(clientWorkspaceId);
    const entities = useTaskCardEntities(entityIds);
    const contextText = joinTaskCardContext({ clientName, entities });
    return contextText ? `${args.task.title}, ${contextText}` : args.task.title;
};
